import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import type {
  ChatBuildErrorItem,
  ChatEvent,
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";
import type { SelectedElementDesignationElement } from "schemas/generated/fileModifiers";

import { useCallback, useEffect, useRef, useState } from "react";

import { isValidChatAssetType } from "@editor/components/editor/ai/chat/utils/chatAssetValidation";
import { trackError } from "@editor/infra/analytics";
import { getTokenFromStorage } from "@editor/infra/auth";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { chatRouter, chatUtils } from "@editor/trpc/chat";
import { fileVersionClient, fileVersionUtils } from "@editor/trpc/fileVersion";
import { funnelUtils } from "@editor/trpc/funnel";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";
import { workspaceUtils } from "@editor/trpc/workspace";
import { getPublisherUrl } from "@editor/utils/env";

import { getAssetUrlsFromFiles } from "@/features/assets/useUploadAsset";
import { useNextDevServer } from "@/features/code-execution/context";
import {
  detect200StatusCode,
  detectBuildError,
  detectFileStatusError,
} from "@/features/code-execution/utils";
import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import { invalidateResourcesRelatedToDeployment } from "@/features/next-gen/components/invalidateResourcesRelatedToDeployment";
import {
  useChatStoreActions,
  useIsChatLoading,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import { serializeChatToken } from "@/features/react-editor/components/chat/utils/chatTokenSerialization";
import { useChatSidebar } from "@/features/react-editor/hooks/useChatSidebar";
import { useSandboxStateActions } from "@/features/react-editor/store/SandboxStateStoreContext";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { errorToast } from "@replo/design-system/components/alert/Toast";
import { skipToken, useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { chatTokenSchema } from "schemas/chatMessage";
import { ReploError } from "schemas/errors";
import { isChatTokenSegment, parseChatTokens } from "schemas/utils/chat";
import { v4 } from "uuid";

import useCurrentProjectId from "../useCurrentProjectId";
import useCurrentUser from "../useCurrentUser";
import { useLogAnalytics } from "../useLogAnalytics";

class FileErrorFromChatError extends ReploError {}
class ChatStreamError extends ReploError {}

const TOOLS_THAT_REQUIRE_FILE_REFETCH = new Set([
  "smallCodeChangeTool",
  "mediumCodeChangeTool",
  "codeChangeSwarmTool",
  "retryCodeChangeTool",
  "generatePageTool",
]);

export const useCurrentChat = () => {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();

  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const isChatLoading = useIsChatLoading(chatId);

  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const { setViewingVersionId } = useSandboxStateActions();

  const {
    setStreamingState,
    getStreamingState,
    addOrUpdateMessageForChat,
    updateToolCallProgressForChat,
    updateStreamingStatus,
  } = useChatStoreActions();

  const forceScrollToBottomRef = useRef<(() => void) | null>(null);
  const { logs } = useNextDevServer();
  const [isMonitoringLogs, setIsMonitoringLogs] = useState(false);
  const [logStartIndex, setLogStartIndex] = useState(0);
  const hasCalledHandleFileErrorRef = useRef(false);

  const [files, setFiles] = useState<File[] | null>(null);

  const { setOpen: setIsChatSidebarOpen } = useChatSidebar();

  const { mutateAsync: createChat } = chatRouter.create.useMutation({
    onSuccess: () => {
      void chatUtils.getPaginatedChats.invalidate({
        projectId,
      });
    },
  });

  const { mutateAsync: updateChatPageId } =
    chatRouter.updateChatPageId.useMutation({
      onSuccess: () => {
        void chatUtils.getPaginatedChats.invalidate({
          projectId,
        });
      },
    });

  const { mutateAsync: generateTitle, isPending: isGeneratingTitle } =
    chatRouter.generateTitle.useMutation({
      onSuccess: () => {
        if (chatId) {
          void chatUtils.get.invalidate({
            chatId,
            projectId,
          });
          void chatUtils.getPaginatedChats.invalidate({
            projectId,
          });
        }
      },
    });

  const { mutateAsync: uploadFiles } = useMutation({
    mutationFn: ({ files, projectId }: { files: File[]; projectId: string }) =>
      getAssetUrlsFromFiles(files, projectId),
    onError: (error) => {
      console.error("Failed to upload files:", error);
    },
  });

  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  const { mutateAsync: revertLatestPageVersion } =
    nextJSProjectRouter.revertLatestPageVersion.useMutation({
      onSuccess: async () => {
        // NOTE (Bobby: 2025-07-11): We set a timeout of 2 seconds here because of some webcontainer race condition where if mountAdditionalFiles is called
        // multiple times in a row, the rendered file will only be the first one. Some hot reload issue. https://linear.app/replo/issue/REPL-17915/investigate-hot-reloading-race-condition-in-web-containers
        // In this case, it's fine to wait 2 seconds since the revert is really fast and it's reasonable to wait 2 seconds for the file to be reverted.
        if (!isServerSandboxEnabled) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          void updatePageFilesInWebContainer({
            pages: [{ id: pageId! }],
          });
        }
        void fileVersionUtils.findByFileId.invalidate({
          projectId,
          fileId: pageId!,
        });
      },
    });

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
          projectId,
        });
        void chatUtils.getPaginatedMessages.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });
  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
          projectId,
        });
        void chatUtils.getPaginatedMessages.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  // NOTE (Bobby: 2025-07-13): We have two different calls for chat here. If there is already a chatId from the url, we want to fetch that one. Since they may be viewing a chat history someone else shared with them
  // If not, we want to get the user's chat OR create one if they've never had a chat for this page.
  const { data: chatFromUrl, error: chatError } = chatRouter.get.useQuery(
    chatId && projectId ? { chatId, projectId } : skipToken,
    // NOTE Bobby (2025-06-12): This is needed beacause our default trpc/react-query staleTime is infinity. This means when we initially fetch
    // with this query key, the messages will be empty/outdated so when you navigate to a different chat and come back,
    // it will be out of date. Easier to set this as 0 and have it refetch on backend every time since it's not expensive and less logic than invalidating everywhere we care to.
    // TODO Bobby (2025-06-12): We should change the default staleTime to 0
    // NOTE Kevin (2025-09-08): Suppress error notifications for chat not found since we handle this gracefully in the navigation useEffect below
    // by creating a new chat for the pageId if there is one.
    {
      staleTime: 0,
      throwOnError: false,
    },
  );

  const chat = chatFromUrl;

  const handleFileError = useCallback(
    async ({ logs }: { logs?: string }) => {
      if (!pageId || !projectId) {
        return;
      }

      const fileVersionsData = await fileVersionClient.findByFileId.query({
        projectId,
        fileId: pageId,
        limit: 2,
      });

      if (fileVersionsData.fileVersions.length < 2) {
        console.error("No previous file version found to revert to");
        return;
      }

      setIsMonitoringLogs(false);
      // NOTE (Bobby: 2025-07-07): This case shouldn't really happen but is here just in case to prevent it from infinitely looping and
      // starting a new chat to undo last changes. Case where this happens is when it tries to undo an error but that doesn't work and it's still in an error state
      if (hasCalledHandleFileErrorRef.current === true) {
        errorToast(
          "Error",
          "There was an error with that last change. Please reach out to support at support@replo.app",
        );
        return;
      }

      hasCalledHandleFileErrorRef.current = true;

      const buildErrorMessageId = v4();

      addMessage({
        id: buildErrorMessageId,
        role: "assistant",
        projectId,
        content: [
          {
            type: "text",
            contentType: "buildError",
            text: JSON.stringify({
              type: "buildError",
              isReverting: true,
            }),
            fileId: pageId ?? undefined,
          },
        ],
        chatId: chatId!,
      });

      const { previousVersionId, latestVersionId } =
        await revertLatestPageVersion({
          projectId,
          fileId: pageId,
        });

      void fileVersionUtils.get.invalidate({
        id: latestVersionId,
        projectId,
        shouldIncludeDeleted: true,
      });

      if (previousVersionId) {
        setViewingVersionId(previousVersionId);

        const buildErrorItem: ChatBuildErrorItem = {
          type: "buildError",
          isReverting: false,
          erroredVersionId: latestVersionId,
          logs,
        };

        updateMessage({
          id: buildErrorMessageId,
          projectId,
          content: [
            {
              type: "text",
              contentType: "buildError",
              text: JSON.stringify(buildErrorItem),
              fileId: pageId ?? undefined,
            },
          ],
        });
      }
    },
    [
      chatId,
      addMessage,
      projectId,
      updateMessage,
      setViewingVersionId,
      pageId,
      revertLatestPageVersion,
    ],
  );

  const handleStreamMessage = useCallback(
    (ev: EventSourceMessage, streamChatId: string) => {
      if (
        parseJsonOrNull(
          localStorage.getItem("replo.debug.v2.logChatStreamEventsToConsole") ??
            "false",
        )
      ) {
        // biome-ignore lint/suspicious/noConsole: allowed for debugging
        console.info("STREAM EVENT", {
          event: ev,
          streamChatId,
        });
      }
      if (ev.event === "message") {
        const chatEvent = JSON.parse(ev.data) as ChatEvent;
        if (chatEvent.type === "message") {
          const messageData = chatEvent.data;
          if (messageData.role === "assistant" || messageData.role === "tool") {
            const firstContentItem = messageData.content[0];
            if (
              firstContentItem?.type === "tool-result" &&
              pageId &&
              TOOLS_THAT_REQUIRE_FILE_REFETCH.has(firstContentItem?.toolName)
            ) {
              if (!isServerSandboxEnabled) {
                void updatePageFilesInWebContainer({
                  pages: [{ id: pageId! }],
                });
              }
              const toolResult = firstContentItem.output.value;
              if ("fileVersionId" in toolResult && toolResult.fileVersionId) {
                setViewingVersionId(toolResult.fileVersionId);
              }

              void nextJSProjectUtils.getPage.invalidate();
              void funnelUtils.find.invalidate();
              void funnelUtils.list.invalidate();
              void fileVersionUtils.findByFileId.invalidate({
                projectId,
                fileId: pageId!,
              });
            }

            addOrUpdateMessageForChat(
              streamChatId,
              messageData.id,
              messageData.content,
              messageData.role,
            );
          }
        } else if (chatEvent.type === "tool-progress") {
          const toolCallId = chatEvent.data.toolCallId;
          const progress = chatEvent.data.progress;
          updateToolCallProgressForChat(streamChatId, toolCallId, progress);
        }
      } else if (ev.event === "done") {
        if (!isServerSandboxEnabled && pageId) {
          void updatePageFilesInWebContainer({
            pages: [{ id: pageId! }],
          }).catch((error) => {
            errorToast(
              "Error",
              "There was an error with that last change. Reverting to previous version.",
            );
            void handleFileError({ logs: undefined });
            const extra = {
              error,
              chatId: streamChatId,
            };
            trackError(
              new FileErrorFromChatError({
                message:
                  "Detected file error from updatePageFilesInWebContainer after chat change",
                additionalData: extra,
              }),
            );
          });
        }

        updateStreamingStatus(streamChatId, false);
        invalidateResourcesRelatedToDeployment();

        void workspaceUtils.getChatMessagesUsage.invalidate();
      } else if (ev.event === "error") {
        console.error("Stream error:", ev.data);
        errorToast(
          "An unknown error has occurred",
          "There was an error processing your chat. Please try again or reach out to support@replo.app if this error persists.",
        );

        updateStreamingStatus(streamChatId, false);
        setIsMonitoringLogs(false);
      }
    },
    [
      addOrUpdateMessageForChat,
      updateToolCallProgressForChat,
      updateStreamingStatus,
      setViewingVersionId,
      updatePageFilesInWebContainer,
      pageId,
      projectId,
      handleFileError,
      isServerSandboxEnabled,
    ],
  );

  const prepareAssetUrls = useCallback(
    async (files: File[] | null) => {
      let assetData: { url: string; isValidChatAsset: boolean }[] = [];

      if (files && files.length > 0) {
        const assetUrls = await uploadFiles({ files, projectId: projectId! });
        assetData = assetUrls.map((url, index) => ({
          url,
          isValidChatAsset: isValidChatAssetType(files[index]!),
        }));
      }

      return [...assetData];
    },
    [uploadFiles, projectId],
  );

  // NOTE (Bobby: 2025-06-24): This function is used to create all the new user messages that will be added to the chat history. It is the user prompt as
  // well as the context items they've selected. This is added to the chat history on the frontend as well as sent to the backend to be persisted.
  const createNewUserMessages = useCallback(
    async ({
      currentPrompt,
      isHidden,
      chatId,
      defaultDesignation,
      filesToUpload,
    }: {
      currentPrompt: string;
      isHidden: boolean;
      chatId: string;
      defaultDesignation?: SelectedElementDesignationElement;
      filesToUpload?: File[] | null;
    }): Promise<{
      messages: ChatMessage[];
      numInlineAssetsAttached: number;
      numExternalAssetsAttached: number;
      numProductsAttached: number;
      numSelectedElementsAttached: number;
    }> => {
      if (!projectId) {
        return {
          messages: [],
          numInlineAssetsAttached: 0,
          numExternalAssetsAttached: 0,
          numProductsAttached: 0,
          numSelectedElementsAttached: 0,
        };
      }

      const contextMessages: ChatMessageContent = [];
      const assetUrls = await prepareAssetUrls(filesToUpload ?? null);

      // This adds the asset urls that are inline in the prompt.
      // TODO (Bobby: 2025-06-27): Separate this out into a helper function
      const segments = parseChatTokens(currentPrompt);
      const promptAssetUrls = filterNulls(
        segments
          .filter(isChatTokenSegment)
          .map((segment) => segment.token)
          .map((token) => {
            if (token.type === "asset") {
              return token.data.url;
            }
            return null;
          }),
      );

      assetUrls.forEach(({ url, isValidChatAsset }) => {
        contextMessages.push({
          type: "text",
          text: `Asset URL: ${url}`,
          isHidden: true,
        });
        contextMessages.push({
          type: "image",
          image: url,
          isValidChatAsset,
        });
      });

      promptAssetUrls.forEach((url) => {
        contextMessages.push({
          type: "image",
          image: url,
          isHidden: true,
        });
      });

      const numProductsAttached = segments.filter(
        (segment) =>
          segment.type === "chat-token" && segment.token.type === "product",
      ).length;

      const selectedElementToken: string | null = (() => {
        if (!defaultDesignation) {
          return null;
        }
        const token = chatTokenSchema.safeParse({
          type: "selected-element",
          data: {
            elementType: defaultDesignation.element.type,
            byteOffset: defaultDesignation.element.byteOffset,
            innerHtml: defaultDesignation.element.innerHtml,
          },
        });
        if (!token.success) {
          return null;
        }
        return serializeChatToken(token.data);
      })();

      return {
        messages: [
          {
            id: v4(),
            content: [
              ...contextMessages,
              {
                type: "text",
                text: selectedElementToken
                  ? `${selectedElementToken} ${currentPrompt}`
                  : currentPrompt,
                isHidden,
                fileId: pageId ?? undefined,
              },
            ],
            createdAt: new Date(),
            feedback: "none",
            role: "user" as const,
            chatId,
          },
        ],
        numInlineAssetsAttached: promptAssetUrls.length,
        numExternalAssetsAttached: assetUrls.length,
        numProductsAttached,
        numSelectedElementsAttached: 0,
      };
    },
    [prepareAssetUrls, projectId, pageId],
  );

  const startChatStream = useCallback(
    async (
      newUserMessages: ChatMessage[],
      streamChatId: string,
      controller: AbortController,
    ) => {
      const PUBLISHER_URL = getPublisherUrl();
      const token = getTokenFromStorage();
      const contextObject = {
        openPageId: pageId,
      };

      const claudeForLowReasoning = isFeatureEnabled(
        "claude-for-low-reasoning",
      );

      const isGeneratePageToolEnabled = isFeatureEnabled("generate-page-tool");

      await fetchEventSource(`${PUBLISHER_URL}/api/v2/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({
          newUserMessages,
          projectId,
          chatId: streamChatId,
          contextObject,
          isGeneratePageToolEnabled,
          claudeForLowReasoning,
        }),
        signal: controller.signal,
        openWhenHidden: true,
        onmessage: (ev) => handleStreamMessage(ev, streamChatId),
        onclose() {
          updateStreamingStatus(streamChatId, false);
        },
        onerror(err) {
          console.error("Streaming error:", err);
          updateStreamingStatus(streamChatId, false);
          // NOTE (Bobby: 2025-09-23): We need to throw an error here because fetchEventSource autoretries errored requests
          // if you don't do anything. We don't want to autoretry since we'll get errors with unique ids.
          // https://github.com/Azure/fetch-event-source?tab=readme-ov-file#usage
          throw new ChatStreamError({
            message: "Error streaming chat",
            additionalData: {
              error: err,
            },
          });
        },
      });
    },
    [projectId, handleStreamMessage, updateStreamingStatus, pageId],
  );

  // NOTE (Bobby: 2025-07-01): This effect is used to monitor new logs from a
  // chat stream. Doing this so we only call handleBuildError from an error
  // caused by a change from chat and NOT some other error. e.g. we don't want
  // to call handleBuildError if the project is misconfigured or something.
  useEffect(() => {
    if (!isMonitoringLogs || !chatId) {
      return;
    }

    const newLogs = logs.slice(logStartIndex);
    const hasFileError = newLogs.some((log) =>
      detectFileStatusError(log.message),
    );
    // NOTE (Bobby: 2025-07-07): Need this separate build error check because
    // when there's build error it doesn't log out the status code
    const hasBuildError = newLogs.some((log) => detectBuildError(log.message));
    const has200StatusCode = newLogs.some((log) =>
      detect200StatusCode(log.message),
    );

    const currentStreamingState = getStreamingState(chatId);

    if ((hasFileError || hasBuildError) && !currentStreamingState.isStreaming) {
      void handleFileError({
        logs: newLogs.map((log) => log.message).join("\n"),
      });
      const extra = {
        fileId: pageId,
        webcontainerLogs: newLogs.map((log) => log.message),
        hasFileError,
        hasBuildError,
        chatId,
      };
      trackError(
        new FileErrorFromChatError({
          message:
            "Detected file error from watching webcontainer logs after chat change",
          additionalData: extra,
        }),
      );
      return;
    }

    if (has200StatusCode && !currentStreamingState.isStreaming) {
      setIsMonitoringLogs(false);
      return;
    }
  }, [
    logs,
    isMonitoringLogs,
    logStartIndex,
    handleFileError,
    pageId,
    chatId,
    getStreamingState,
  ]);

  const sendChatRequest = useCallback(
    async ({
      prompt,
      isHidden,
      defaultDesignation,
      filesToUpload,
    }: {
      prompt: string;
      isHidden: boolean;
      defaultDesignation?: SelectedElementDesignationElement;
      filesToUpload?: File[] | null;
    }) => {
      if (isChatLoading || !projectId || !user?.id || !prompt) {
        return;
      }

      const chatIdToUse = chatId && chatId.length > 0 ? chatId : v4();

      const controller = new AbortController();
      setStreamingState(chatIdToUse, {
        isStreaming: true,
        controller,
      });

      const {
        messages: newUserMessages,
        numInlineAssetsAttached,
        numExternalAssetsAttached,
        numProductsAttached,
        numSelectedElementsAttached,
      } = await createNewUserMessages({
        currentPrompt: prompt,
        isHidden,
        chatId: chatIdToUse,
        defaultDesignation,
        filesToUpload,
      });
      logEvent("chat.send", {
        origin: "chat",
        userPrompt: prompt,
        numInlineAssetsAttached,
        numExternalAssetsAttached,
        numProductsAttached,
        numSelectedElementsAttached,
      });
      newUserMessages.forEach((message) => {
        addOrUpdateMessageForChat(
          chatIdToUse,
          message.id,
          message.content,
          message.role,
        );
      });

      void workspaceUtils.getChatMessagesUsage.invalidate();

      if (!chatId) {
        try {
          const newChat = await createChat({
            projectId,
            id: chatIdToUse,
          });

          if (pageId) {
            void updateChatPageId({
              projectId,
              chatId: newChat.id,
              pageId,
            });
          }

          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("chatId", newChat.id);
            return newParams;
          });

          setIsChatSidebarOpen(false);
        } catch {
          updateStreamingStatus(chatIdToUse, false);
          errorToast("Error", "Failed to create chat. Please try again.");
          return;
        }
      } else {
        if (pageId) {
          void updateChatPageId({
            projectId,
            chatId,
            pageId,
          });
        }
      }

      setLogStartIndex(logs.length);
      setIsMonitoringLogs(true);
      hasCalledHandleFileErrorRef.current = false;

      try {
        if (
          chatIdToUse &&
          (!chat?.title || chat.title === "Untitled") &&
          projectId
        ) {
          void generateTitle({
            id: chatIdToUse,
            projectId,
            initialPrompt: prompt,
          }).catch((error) => {
            console.warn("Failed to generate chat title:", error);
          });
        }

        // Force scroll to bottom to show the new message after it's added
        if (forceScrollToBottomRef.current) {
          forceScrollToBottomRef.current();
        }

        // Start streaming
        await startChatStream(newUserMessages, chatIdToUse, controller);
      } catch (error) {
        setIsMonitoringLogs(false);
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Chat submission error:", error);
        }

        updateStreamingStatus(chatIdToUse, false);
      }
    },
    [
      createChat,
      isChatLoading,
      projectId,
      user?.id,
      logs.length,
      createNewUserMessages,
      startChatStream,
      logEvent,
      updateStreamingStatus,
      setStreamingState,
      chatId,
      pageId,
      updateChatPageId,
      setSearchParams,
      chat?.title,
      generateTitle,
      setIsChatSidebarOpen,
      addOrUpdateMessageForChat,
    ],
  );

  return {
    sendChatRequest,
    isGeneratingTitle,
    chatId,
    files,
    setFiles,
    chat,
    chatError,
    forceScrollToBottomRef,
  };
};
